import { getSupabase } from '../supabaseClient';
import type { HabitEntity } from '../../types/data';
import { fetchAllPages } from './pagination';
import type { SyncCursor } from './syncCursor';

const TABLE_HABITS = 'habits';

const stripAuditFields = <T extends { created_at?: unknown; modified_at?: unknown }>(input: T): Omit<T, 'created_at' | 'modified_at'> => {
  const { created_at, modified_at, ...rest } = input;
  return rest;
};

export const habitRepo = {
  async listHabits(): Promise<HabitEntity[]> {
    const supabase = getSupabase();
    try {
      return await fetchAllPages<HabitEntity>(() =>
        supabase
          .from(TABLE_HABITS)
          .select('*')
          .order('sort_order', { ascending: true })
          .order('created_at', { ascending: true })
          .order('id', { ascending: true })
      );
    } catch {
      // Backward-compatible fallback for environments where sort_order is not deployed yet.
    }

    return await fetchAllPages<HabitEntity>(() =>
      supabase
        .from(TABLE_HABITS)
        .select('*')
        .order('created_at', { ascending: true })
        .order('id', { ascending: true })
    );
  },

  async listHabitsModifiedAfter(cursor: SyncCursor): Promise<HabitEntity[]> {
    const supabase = getSupabase();
    return await fetchAllPages<HabitEntity>(() =>
      applyModifiedAtIdCursor(
        supabase
        .from(TABLE_HABITS)
        .select('*'),
        cursor
      )
        .order('modified_at', { ascending: true })
        .order('id', { ascending: true })
    );
  },

  async findBySlug(slug: string): Promise<HabitEntity | null> {
    const supabase = getSupabase();
    const { data, error } = await supabase
      .from(TABLE_HABITS)
      .select('*')
      .eq('slug', slug)
      .eq('is_deleted', false)
      .maybeSingle();
    if (error) throw error;
    return (data as HabitEntity) ?? null;
  },

  async createHabit(input: Omit<HabitEntity, 'id' | 'created_at' | 'modified_at' | 'is_deleted'> & { slug?: string }): Promise<HabitEntity> {
    const supabase = getSupabase();
    const row = {
      title: input.title,
      body: input.body,
      is_deleted: false,
      last_done_on: input.last_done_on,
      done_cycle_date: input.done_cycle_date,
      slug: input.slug,
      sort_order: input.sort_order,
    };
    const { data, error } = await supabase
      .from(TABLE_HABITS)
      .insert(row as any)
      .select()
      .single();
    if (error) throw error;
    return data as HabitEntity;
  },

  async updateHabit(id: string, patch: Partial<HabitEntity>): Promise<HabitEntity | null> {
    const supabase = getSupabase();
    const toUpdate = stripAuditFields(patch);
    if (Object.keys(toUpdate).length === 0) return null;
    const { data, error } = await supabase
      .from(TABLE_HABITS)
      .update(toUpdate)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as HabitEntity;
  },

  async softDeleteHabit(id: string): Promise<void> {
    const supabase = getSupabase();
    const { error } = await supabase
      .from(TABLE_HABITS)
      .update({ is_deleted: true })
      .eq('id', id);
    if (error) throw error;
  },
};

const applyModifiedAtIdCursor = (query: any, cursor: SyncCursor): any => {
  const id = cursor.tieBreaker[0];
  if (!id) return query.gte('modified_at', cursor.modifiedAt);

  return query.or(`modified_at.gt.${cursor.modifiedAt},and(modified_at.eq.${cursor.modifiedAt},id.gt.${id})`);
};
